import { Download, Trash2, FileText } from 'lucide-react'
import { useFormulaStore } from '../formulaStore'
import { clsx } from 'clsx'
import { showToast } from '@store/uiStore'
import { useConfirm } from '@hooks/useConfirm'

export function AuditLogTab() {
  const { auditLog, clearAuditLog } = useFormulaStore()
  const { confirm, ConfirmDialog } = useConfirm()

  const handleExport = () => {
    if (auditLog.length === 0) {
      showToast('No audit log entries to export', 'warning')
      return
    }

    let csv = 'FORMULA AUDIT LOG\n'
    csv += `Exported: ${new Date().toISOString()}\n\n`
    csv += 'Timestamp,User,Action,Formula,Old Value,New Value\n'

    auditLog.forEach((entry) => {
      csv += `"${entry.timestamp}","${entry.userName}","${entry.action}","${entry.formulaName}","${entry.oldValue || ''}","${entry.newValue || ''}"\n`
    })

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `formula_audit_log_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const handleClear = async () => {
    const ok = await confirm({
      title: 'Clear audit log',
      message: 'Are you sure you want to clear the entire audit log? This cannot be undone.',
      confirmLabel: 'Clear',
      variant: 'danger',
    })
    if (ok) clearAuditLog()
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-4">
      <ConfirmDialog />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-[var(--text-primary)]">Formula Change Audit Log</h3>
          <p className="text-[11px] text-[var(--text-muted)] mt-1">
            Track all changes made to calculation formulas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={auditLog.length === 0}
            className={clsx(
              'flex items-center gap-2 px-3 py-2 text-[11px] rounded-lg transition-colors',
              auditLog.length > 0
                ? 'text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                : 'text-gray-600 cursor-not-allowed'
            )}
          >
            <Download size={14} />
            Export CSV
          </button>
          <button
            onClick={handleClear}
            disabled={auditLog.length === 0}
            className={clsx(
              'flex items-center gap-2 px-3 py-2 text-[11px] rounded-lg transition-colors',
              auditLog.length > 0
                ? 'text-danger hover:bg-danger/10'
                : 'text-gray-600 cursor-not-allowed'
            )}
          >
            <Trash2 size={14} />
            Clear Log
          </button>
        </div>
      </div>

      {/* Audit Log List */}
      <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        {auditLog.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
            <FileText size={48} strokeWidth={1} className="mb-4 opacity-50" />
            <p className="text-sm">No formula changes recorded yet</p>
            <p className="text-[11px] text-gray-600 mt-1">
              Changes to formulas will be logged here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-dark-border max-h-[500px] overflow-y-auto">
            {auditLog.map((entry) => (
              <div key={entry.id} className="p-4 hover:bg-[var(--bg-secondary)]/50 transition-colors">
                {/* Header */}
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[11px] font-medium text-[var(--text-primary)]">{entry.userName}</span>
                  <span
                    className={clsx(
                      'text-[9px] px-1.5 py-0.5 rounded font-medium',
                      entry.action === 'modified' && 'bg-primary/20 text-primary',
                      entry.action === 'reset' && 'bg-warning/20 text-warning',
                      entry.action === 'reset_all' && 'bg-danger/20 text-danger'
                    )}
                  >
                    {entry.action === 'reset_all'
                      ? 'Reset All'
                      : entry.action === 'reset'
                        ? 'Reset'
                        : 'Modified'}
                  </span>
                  <span className="text-[10px] text-[var(--text-muted)] ml-auto">
                    {formatDate(entry.timestamp)}
                  </span>
                </div>

                {/* Formula Name */}
                <div className="text-sm text-[var(--text-primary)] font-medium mb-2">{entry.formulaName}</div>

                {/* Changes */}
                {entry.action !== 'reset_all' ? (
                  <div className="space-y-1">
                    {entry.oldValue && (
                      <div className="flex items-start gap-2 text-[11px]">
                        <span className="text-[var(--text-muted)] w-10 flex-shrink-0">From:</span>
                        <code className="text-danger/80 bg-danger/10 px-1.5 py-0.5 rounded break-all">
                          {entry.oldValue}
                        </code>
                      </div>
                    )}
                    {entry.newValue && (
                      <div className="flex items-start gap-2 text-[11px]">
                        <span className="text-[var(--text-muted)] w-10 flex-shrink-0">To:</span>
                        <code className="text-success/80 bg-success/10 px-1.5 py-0.5 rounded break-all">
                          {entry.newValue}
                        </code>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-[11px] text-[var(--text-muted)]">All formulas reset to defaults</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
